import { computed, makeObservable, observable } from "mobx"

import type { IEventBus } from "@/domains/events"

import type { ILockLookup, ILockRepository } from "./interfaces"
import type { ILock } from "./types"
import { LockAddedToRepositoryEvent } from "./events"
import { LockStore } from "./lock-store"

export class LockRepository implements ILockRepository, ILockLookup {
  locks = new Map<number, LockStore>()

  constructor(private readonly eventBus: IEventBus) {
    makeObservable(this, {
      locks: observable,

      // Computed properties
      totalLocks: computed,
      totalLockValue: computed,
      totalLockPower: computed,
      countAll: computed,
      countCanMaxLock: computed,
      countVoted: computed,
      countNotVoted: computed,
      countScalest: computed,
      countScalar: computed,
    })
  }

  add(lock: LockStore): void {
    const existing = this.find(lock.id)
    if (existing) {
      return
    }

    this.locks.set(lock.id, lock)
    this.eventBus.publish(new LockAddedToRepositoryEvent(lock.id))
  }

  find(id: number): ILock | undefined {
    return this.locks.get(id)
  }

  // Get all locks
  getAll(): ILock[] {
    return Array.from(this.locks.values())
  }

  /**
   * Get all locks except the one with the given ID
   * @param id
   * @returns
   */
  getOthers(id: number): ILock[] {
    return Array.from(this.locks.values()).filter((lock) => lock.id !== id)
  }

  get totalLocks(): number {
    return this.locks.size
  }

  get totalLockValue(): number {
    return Array.from(this.locks.values()).reduce((total, lock) => {
      return total + lock.lockValue
    }, 0)
  }

  get totalLockPower(): number {
    return Array.from(this.locks.values()).reduce((total, lock) => {
      return total + lock.power
    }, 0)
  }

  removeLock(id: number): void {
    const lock = this.find(id)

    if (lock) {
      // Dispose the lock to stop queries and clear rewards
      lock.dispose()
      // Remove from map
      this.locks.delete(id)
    }
  }

  clearAll(): void {
    // Dispose each lock to stop queries and clear rewards
    for (const lock of this.locks.values()) {
      lock.dispose()
    }

    // Now clear the map
    this.locks.clear()
  }

  get countAll(): number {
    return this.locks.size
  }

  get countCanMaxLock(): number {
    return Array.from(this.locks.values()).filter((nft) => !nft.isMaxLocked && !nft.hasExpired).length
  }

  get countVoted(): number {
    return Array.from(this.locks.values()).filter((nft) => nft.hasVotedThisEpoch).length
  }

  get countNotVoted(): number {
    return Array.from(this.locks.values()).filter((nft) => !nft.hasVotedThisEpoch && !nft.hasExpired).length
  }

  get countScalest(): number {
    return Array.from(this.locks.values()).filter((nft) => nft.isScalest).length
  }

  get countScalar(): number {
    return Array.from(this.locks.values()).filter((nft) => nft.isScalar).length
  }
}
